import type { control, layer, source } from 'azure-maps-control';
import type { IAzureMapsContextProps } from 'react-azure-maps';

type AzureMap = NonNullable<IAzureMapsContextProps['mapRef']>;

interface MapResources {
  lineLayer: layer.LineLayer;
  datasource: source.DataSource;
  zoomControl: control.ZoomControl;
}

/**
 * Take a controller's own layer, data source and zoom control back off the map,
 * so a replacement map instance can be set up from scratch: the layer id is
 * fixed, so azure-maps would reject a second layer called 'line' and the
 * controls would stack up.
 *
 * Does nothing once the container the map was mounted into has left the DOM —
 * the dialog or tab hosting it closed before the effect cleanup ran. Such a map
 * is on its way out anyway, and azure-maps-control throws from `layers`,
 * `sources` and `controls`; since nothing in this app has an ErrorBoundary,
 * that exception would take the whole React tree down.
 */
const cleanMap = (
  mapRef: AzureMap,
  { lineLayer, datasource, zoomControl }: MapResources
) => {
  if (!mapRef.getMapContainer()?.isConnected) {
    return;
  }

  mapRef.layers.remove(lineLayer);
  mapRef.sources.remove(datasource);
  mapRef.controls.remove(zoomControl);
};

export default cleanMap;
